import { useEffect, useState } from "react";
import { useMe } from "@/data/user.ts";
import { useSEO } from "@/contexts/SeoContext.tsx";
import { useBreakpoint } from "@/utils/hooks/useBreakpoints.ts";
import useGlobals from "@/utils/hooks/useGlobals.ts";
import dayjs from "dayjs";
import { useTutorAvailabilities } from "@/data/one-to-one.ts";
import { isSameDay, parseISO } from "date-fns";
import { useUserExercises } from "@/data/exercises.ts";
import BookLiveCorrectionDesktopView from "@/components/exercises/correction/DesktopView.tsx";
import BookLiveCorrectionMobileView from "@/components/exercises/correction/MobileView.tsx";
import DashboardContentLayout from "@/layouts/DashboardContentLayout.tsx";

export default function BookLiveCorrection() {
  const { me } = useMe();
  const [countCorrection, setCountCorrections] = useState<number>(0);
  const [countBookedCorrection, setCountBookedCorrections] =
    useState<number>(0);
  const [hoursWriting, setHoursWriting] = useState<number>(0);
  const { setTitle, setDescription } = useSEO();
  const { setGlobalState } = useGlobals();
  const breakpoint = useBreakpoint();
  const isSmallScreen = ["xs", "sm", "md"].indexOf(breakpoint) !== -1;

  const [date, setDate] = useState(dayjs());
  const [newAvailabilities, setNewAvailabilities] = useState<
    { date: string; tutor: string }[]
  >([]);

  const { exercises, isLoading: loadingExercises } = useUserExercises();

  const { availabilities, isLoading } = useTutorAvailabilities({
    tutor_role: "Writing & Mock test",
    half_time: true,
    time_distance: 12,
  });

  const filteredAvailableDays = newAvailabilities?.filter((el) =>
    isSameDay(parseISO(el.date), parseISO(date?.format("YYYY-MM-DD"))),
  );

  const calendarDaysToShow = [
    ...new Set(
      newAvailabilities?.map((el) => dayjs(el.date, "YYYY-MM-DD HH:mm")),
    ),
  ];

  useEffect(() => {
    setGlobalState("backgroundClass", "bg-brand-background");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setTitle(
      `${me?.item.user.first_name} ${me?.item.user.last_name} | Live Correction `,
    );
    setDescription("Book Live Correction");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me]);

  useEffect(() => {
    const availabilitiesUnique: { date: string; tutor: string }[] = [];

    availabilities?.items?.forEach((el: { date: string; tutor: string }) => {
      // Check if the date is already in the availabilitiesUnique array
      const index = availabilitiesUnique.findIndex(
        (item) => item.date === el.date,
      );

      if (index === -1) {
        // If the date is not found, push the object into availabilitiesUnique array
        availabilitiesUnique.push(el);
      }
    });

    // Sort the availabilitiesUnique array based on the date property
    availabilitiesUnique.sort((a, b) => {
      return new Date(a.date).getTime() - new Date(b.date).getTime();
    });

    setNewAvailabilities(availabilitiesUnique);
  }, [availabilities?.items]);

  useEffect(() => {
    const user = me?.item.user;
    const month = me?.item.months_writing_correction;

    if (user && exercises && month) {
      // if (user.unlock_count_exercises) {
      //   const currentMonth = dayjs().format("YYYY-MM");
      //   let countBooked = 0;
      //   exercises.item.count_booked_corrections.forEach((el) => {
      //     if (el.month === currentMonth) {
      //       countBooked += el.corrections;
      //     }
      //   });
      //   setCountCorrections(countBooked);
      // }

      if (!user.unlock_count_exercises && month) {
        let sumCorrection = 0;
        let sumBookedCorrection = 0;
        let sumHours = 0;

        month.forEach((el) => {
          sumCorrection += el.count_corrections;
          sumBookedCorrection += el.count_booked_corrections;
          sumHours += el.hours_writing_correction;
        });

        setCountCorrections(sumCorrection);
        setCountBookedCorrections(sumBookedCorrection);
        setHoursWriting(sumHours);
      }
    }
  }, [me, exercises]);

  return (
    <DashboardContentLayout
      loading={isLoading || loadingExercises}
      className="px-0"
    >
      {isSmallScreen ? (
        <BookLiveCorrectionMobileView
          date={date}
          setDate={setDate}
          availableDays={calendarDaysToShow}
          availableDaysArray={filteredAvailableDays || []}
          hoursWriting={hoursWriting}
          countCorrections={countCorrection}
          countBookedCorrections={countBookedCorrection}
        />
      ) : (
        <BookLiveCorrectionDesktopView
          date={date}
          setDate={setDate}
          availableDays={calendarDaysToShow}
          availableDaysArray={filteredAvailableDays || []}
          hoursWriting={hoursWriting}
          countCorrections={countCorrection}
          countBookedCorrections={countBookedCorrection}
        />
      )}
    </DashboardContentLayout>
  );
}
